/**
 * Karte der Objektposition. Im Normalfall ist hier nichts zu tun — die Koordinaten ermittelt
 * der Nachlauf selbst. Der ziehbare Pin ist das Korrekturwerkzeug fuer die Faelle, in denen
 * der Anbieter danebenliegt: Neubaugebiete, laendliche Adressen, uneindeutige Hausnummern.
 * Kennt der Anbieter das Haus gar nicht, zeigt die Karte die Umgebung: Ein Klick setzt dort einen
 * vorlaeufigen Pin, uebernommen wird er erst per Knopf — ein Klick beim Zoomen ist keine Entscheidung.
 */
import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Die Symbole liefert StayOn selbst aus: ein fremder Server sähe sonst die IP jedes Betrachters.
const ICON = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

export interface MapArea {
  latitude: number;
  longitude: number;
  zoom: number;
}

interface Props {
  /** Der gespeicherte Pin. Fehlt er, zeigt die Karte `area`. */
  latitude?: number;
  longitude?: number;
  area?: MapArea;
  /** Den gespeicherten Pin ziehen; ohne Handler bleibt er fest — so auch im Apartment. */
  onMoved?: (latitude: number, longitude: number) => void;
  /** Ohne Pin: wo der vorlaeufige Pin steht und wie weit hineingezoomt ist. */
  onPicked?: (latitude: number, longitude: number, zoom: number) => void;
  zoom?: number;
}

export function ObjectLocationMap(props: Props) {
  const { latitude, longitude, area, onMoved, onPicked, zoom = 16 } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  // Ueber Refs, damit ein neuer Handler die Karte nicht neu aufbaut und den Pin zuruecksetzt.
  const onMovedRef = useRef(onMoved);
  onMovedRef.current = onMoved;
  const onPickedRef = useRef(onPicked);
  onPickedRef.current = onPicked;
  const draggable = !!onMoved;
  const picking = !!onPicked;
  const areaLatitude = area?.latitude;
  const areaLongitude = area?.longitude;
  const areaZoom = area?.zoom;

  useEffect(() => {
    if (!containerRef.current) return;
    const pin: [number, number] | null =
      typeof latitude === 'number' && typeof longitude === 'number' ? [latitude, longitude] : null;
    const view: [number, number] | null =
      pin ??
      (typeof areaLatitude === 'number' && typeof areaLongitude === 'number'
        ? [areaLatitude, areaLongitude]
        : null);
    if (!view) return;

    const map = L.map(containerRef.current, {
      center: view,
      zoom: pin ? zoom : (areaZoom ?? zoom),
      scrollWheelZoom: false,
      attributionControl: false,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    if (pin) {
      const marker = L.marker(pin, { icon: ICON, draggable }).addTo(map);
      if (draggable) {
        marker.on('dragend', () => {
          const { lat, lng } = marker.getLatLng();
          onMovedRef.current?.(lat, lng);
        });
      }
    } else if (picking) {
      let picked: L.Marker | null = null;
      const report = () => {
        if (!picked) return;
        const { lat, lng } = picked.getLatLng();
        onPickedRef.current?.(lat, lng, map.getZoom());
      };
      map.on('click', (event: L.LeafletMouseEvent) => {
        if (picked) {
          picked.setLatLng(event.latlng);
        } else {
          picked = L.marker(event.latlng, { icon: ICON, draggable: true }).addTo(map);
          picked.on('dragend', report);
        }
        report();
      });
      map.on('zoomend', report);
    }

    return () => {
      map.remove();
    };
  }, [latitude, longitude, areaLatitude, areaLongitude, areaZoom, zoom, draggable, picking]);

  return (
    <div className="border-line/60 relative overflow-hidden rounded border">
      <div
        ref={containerRef}
        className={picking ? 'h-64 w-full cursor-crosshair' : 'h-64 w-full'}
        data-testid="object-location-map"
      />
    </div>
  );
}
